import GermanDateInput from "@/components/GermanDateInput";
import React, { useEffect, useState } from "react";
import { ClientCertificates } from "@/components/ClientCertificates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function WeiterbildungKundeDialog({ open, onOpenChange, initialData, onSaved }) {
  const editing = !!initialData;
  const [weiterbildungen, setWeiterbildungen] = useState([]);
  const [form, setForm] = useState(() => ({
    name: "", email: "", phone: "",
    street: "", postal_code: "", city: "",
    customer_number: "", measure_number: "",
    measure_id: "", module_ids: [],
    birth_date: "", kostentraeger: "", notes: "",
    start_date: "", end_date: "",
  }));
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/weiterbildung/measures");
        setWeiterbildungen((r.data || []).filter((m) => m.active));
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        name: initialData.name || `${initialData.first_name || ""} ${initialData.last_name || ""}`.trim(),
        email: initialData.email || "", phone: initialData.phone || "",
        street: initialData.street || "", postal_code: initialData.postal_code || "", city: initialData.city || "",
        customer_number: initialData.customer_number || "", measure_number: initialData.measure_number || "",
        measure_id: initialData.measure_id || "", module_ids: initialData.module_ids || [],
        birth_date: initialData.birth_date || "", kostentraeger: initialData.kostentraeger || "", notes: initialData.notes || "",
        start_date: initialData.start_date || "", end_date: initialData.end_date || "",
      });
    } else {
      setForm({ name: "", email: "", phone: "", street: "", postal_code: "", city: "", customer_number: "", measure_number: "", measure_id: "", module_ids: [], birth_date: "", kostentraeger: "", notes: "", start_date: "", end_date: "" });
    }
    setInviteResult(null);
  }, [open, initialData]);

  const pickWeiterbildung = (measure_id) => setForm((f) => ({ ...f, measure_id, module_ids: [] }));

  const toggleModule = (moduleId) => {
    setForm((f) => {
      const has = f.module_ids.includes(moduleId);
      return { ...f, module_ids: has ? f.module_ids.filter((id) => id !== moduleId) : [...f.module_ids, moduleId] };
    });
  };

  const selectedWeiterbildung = weiterbildungen.find((m) => m.measure_id === form.measure_id);
  const selectedModules = (selectedWeiterbildung?.modules || []).filter((m) => form.module_ids.includes(m.module_id));
  const totalUe = selectedModules.reduce((s, m) => s + (m.ue || 0), 0);
  const totalPrice = selectedModules.reduce((s, m) => s + (m.total_price || 0), 0);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Bitte Name angeben"); return; }
    if (!editing && !form.email.trim()) { toast.error("Bitte E-Mail angeben — der Kunde bekommt einen Einladungslink"); return; }
    if (!form.phone.trim()) { toast.error("Bitte Telefonnummer angeben"); return; }
    if (!form.street.trim() || !form.postal_code.trim() || !form.city.trim()) { toast.error("Bitte vollständige Adresse angeben (Straße, PLZ, Ort)"); return; }
    if (!form.customer_number.trim()) { toast.error("Bitte Kundennummer angeben"); return; }
    if (!form.measure_number.trim()) { toast.error("Bitte Maßnahmennummer angeben"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), phone: form.phone, street: form.street, postal_code: form.postal_code, city: form.city,
        customer_number: form.customer_number, measure_number: form.measure_number,
        measure_id: form.measure_id || null, module_ids: form.module_ids,
        birth_date: form.birth_date || "", kostentraeger: form.kostentraeger || "", notes: form.notes || "",
        start_date: form.start_date || "", end_date: form.end_date || "",
      };
      if (!editing) payload.email = form.email.trim();
      const { data } = editing
        ? await api.put(`/weiterbildung/participants/${initialData.participant_id}`, payload)
        : await api.post("/weiterbildung/participants", payload);
      toast.success(editing ? "Kunde aktualisiert" : "Kunde angelegt & eingeladen");
      onSaved?.(data);
      if (!editing && data.invite_link) setInviteResult(data);
      else onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const copyInvite = async () => {
    if (!inviteResult?.invite_link) return;
    try { await navigator.clipboard.writeText(inviteResult.invite_link); toast.success("Einladungslink kopiert"); }
    catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-none border border-slate-200 p-0" data-testid="wb-kunde-dialog">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">{editing ? "Kunde bearbeiten" : "Neuer Kunde"}</div>
          <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">Weiterbildungs-Teilnehmer</DialogTitle>
          <DialogDescription className="sr-only">Lege einen neuen Weiterbildungs-Kunden an oder bearbeite einen bestehenden.</DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 space-y-5 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Name *</label>
            <input data-testid="wb-kunde-name-input" className="input-tech" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          {!editing ? (
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">E-Mail * (Einladung wird verschickt)</label>
              <input data-testid="wb-kunde-email-input" type="email" className="input-tech font-mono" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <p className="text-xs text-slate-500 mt-2">Der Kunde bekommt einen Einladungslink zum Passwort-Setzen.</p>
            </div>
          ) : (
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">E-Mail</label>
              <input data-testid="wb-kunde-email-input" type="email" disabled className="input-tech font-mono bg-slate-100 text-slate-500" value={form.email} />
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Telefon *</label>
            <input data-testid="wb-kunde-phone-input" type="tel" className="input-tech font-mono" placeholder="+49 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Adresse (für Rechnung) *</label>
            <input data-testid="wb-kunde-street-input" className="input-tech" placeholder="Straße und Hausnummer" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
            <div className="grid grid-cols-[130px_1fr] gap-3 mt-3">
              <input data-testid="wb-kunde-postal-input" className="input-tech font-mono" placeholder="PLZ" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
              <input data-testid="wb-kunde-city-input" className="input-tech" placeholder="Ort" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kundennummer *</label>
              <input data-testid="wb-kunde-customer-input" className="input-tech font-mono" value={form.customer_number} onChange={(e) => setForm({ ...form, customer_number: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Maßnahmennummer *</label>
              <input data-testid="wb-kunde-measure-input" className="input-tech font-mono" value={form.measure_number} onChange={(e) => setForm({ ...form, measure_number: e.target.value })} />
            </div>
          </div>

          {/* --- Weiterbildung + Module --- */}
          <div className="border-t border-slate-200 pt-5">
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Weiterbildung *</label>
            <select data-testid="wb-kunde-measure-select" className="input-tech" value={form.measure_id} onChange={(e) => pickWeiterbildung(e.target.value)}>
              <option value="">— keine Weiterbildung aus Katalog —</option>
              {weiterbildungen.map((m) => (
                <option key={m.measure_id} value={m.measure_id}>{`${m.name} · ${m.modules.length} Module`}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-2">
              Wähle eine Weiterbildung → dann kreuze die <b>Module</b> an, die dieser Kunde bucht. UE und Preis/UE
              sind je Modul fest vorgegeben (zertifizierte Maßnahme) und können nicht verändert werden.
            </p>

            {selectedWeiterbildung && (
              <div className="mt-4 border border-slate-200 divide-y divide-slate-100" data-testid="wb-kunde-modules-picker">
                {(selectedWeiterbildung.modules || []).length === 0 && (
                  <div className="p-3 text-xs text-slate-400 italic">Diese Weiterbildung hat noch keine Module im Katalog.</div>
                )}
                {(selectedWeiterbildung.modules || []).map((mod) => {
                  const checked = form.module_ids.includes(mod.module_id);
                  return (
                    <label key={mod.module_id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${checked ? "bg-[#0a788c]/5" : ""}`} data-testid={`wb-kunde-module-${mod.module_id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleModule(mod.module_id)} className="accent-[#00CCC9] w-4 h-4" />
                      <span className="flex-1 min-w-0 text-sm text-slate-900 truncate">{mod.name}</span>
                      <span className="text-[11px] text-slate-500 font-mono w-16 text-right">{mod.ue} UE</span>
                      <span className="text-[11px] text-slate-500 font-mono w-20 text-right">{mod.price_per_ue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/UE</span>
                      <span className="text-[11px] text-slate-900 font-mono font-medium w-24 text-right">{mod.total_price.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                    </label>
                  );
                })}
                <div className="p-3 bg-slate-900 text-white text-xs font-mono flex justify-between" data-testid="wb-kunde-modules-summary">
                  <span className="uppercase tracking-overline text-[10px] font-bold">Summe gebuchte Module</span>
                  <span>{selectedModules.length} Module · {totalUe} UE gesamt{totalPrice > 0 ? ` · ${totalPrice.toLocaleString("de-DE", { minimumFractionDigits: 2 })} € gesamt` : ""}</span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 pt-6">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-3">Vertragsdaten (für die automatische Vertrags-Erstellung)</div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Geburtsdatum (optional)</label>
                <GermanDateInput data-testid="wb-kunde-birth-input" className="input-tech font-mono" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kostenträger (optional)</label>
                <input data-testid="wb-kunde-kostentraeger-input" className="input-tech" placeholder="z. B. Jobcenter Berlin Mitte" value={form.kostentraeger} onChange={(e) => setForm({ ...form, kostentraeger: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Beginn Maßnahme (optional)</label>
                <GermanDateInput data-testid="wb-kunde-start-input" className="input-tech font-mono" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Ende Maßnahme (optional)</label>
                <GermanDateInput data-testid="wb-kunde-end-input" className="input-tech font-mono" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Wird für den Vertrag genutzt, wenn der Kunde (noch) keiner Klasse zugewiesen ist, oder überschreibt
              den Zeitraum der zugewiesenen Klasse. Ohne Angabe zählt automatisch der Zeitraum der Klasse.
            </p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Notizen</label>
            <textarea data-testid="wb-kunde-notes-input" className="input-tech min-h-[70px] text-xs" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Interne Notizen — nur für Admin/Manager sichtbar." />
          </div>
        </div>

        <DialogFooter className="px-8 py-5 border-t border-slate-100 flex gap-3">
          {inviteResult ? (
            <>
              <div className="flex-1 text-xs font-mono text-slate-700 bg-slate-50 p-3 border border-slate-200 break-all" data-testid="wb-kunde-invite-link">{inviteResult.invite_link}</div>
              <button data-testid="wb-kunde-invite-copy" onClick={copyInvite} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">Kopieren</button>
              <button data-testid="wb-kunde-invite-done" onClick={() => onOpenChange(false)} className="px-6 py-3 text-sm font-medium border border-slate-300">Fertig</button>
            </>
          ) : (
            <>
              <button data-testid="wb-kunde-cancel" onClick={() => onOpenChange(false)} className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">Abbrechen</button>
              <button data-testid="wb-kunde-save" onClick={submit} disabled={saving} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50">
                {saving ? "Speichert…" : editing ? "Aktualisieren" : "Kunde anlegen & einladen"}
              </button>
            </>
          )}
        </DialogFooter>
        {editing && initialData?.participant_id && <div className="px-6 pb-6"><ClientCertificates clientId={initialData.participant_id} canManage /></div>}
      </DialogContent>
    </Dialog>
  );
}
